import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { MonitorPlay, MapPin, Edit, Trash, Bookmark, BookmarkCheck, Users, X } from "lucide-react";
import { Screen } from "@/models/screen";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

interface ScreenCardProps {
  screen: Screen;
  onEdit?: (screen: Screen) => void;
}

const ScreenCard: React.FC<ScreenCardProps> = ({ screen, onEdit }) => {
  const { getActiveAdsByScreenId, getExpiredAdsByScreenId, deleteScreen, toggleFavoriteScreen, isFavoriteScreen, hideExpiredAd } = useData();
  const { isAdmin, user } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDeleteAdDialogOpen, setConfirmDeleteAdDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedExpiredAdId, setSelectedExpiredAdId] = useState<string | null>(null);
  const [isHidingAd, setIsHidingAd] = useState(false);
  const isFavorite = isFavoriteScreen(screen.id);

  const activeAds = getActiveAdsByScreenId(screen.id);
  const expiredAds = getExpiredAdsByScreenId(screen.id);

  // Calculate the actual occupied spaces (only counting active ads)
  const actualOccupiedSpaces = activeAds.length;
  
  // Calculate the actual available spaces (total - active ads)
  const actualAvailableSpaces = screen.totalSpaces - actualOccupiedSpaces;
  
  // Calculate the actual occupancy rate
  const actualOccupancyRate = screen.totalSpaces > 0 
    ? Math.round((actualOccupiedSpaces / screen.totalSpaces) * 100) 
    : 0;

  // Determine background gradient color based on occupancy rate
  let gradientColor = "from-blue-500 to-purple-500";
  if (actualOccupancyRate >= 80) {
    gradientColor = "from-orange-400 to-red-500";
  } else if (actualOccupancyRate >= 50) {
    gradientColor = "from-yellow-400 to-orange-500";
  }

  // Circle progress stroke color
  let strokeColor = "#33C3F0"; // Default blue
  if (actualOccupancyRate >= 80) {
    strokeColor = "#ea384c"; // Red
  } else if (actualOccupancyRate >= 50) {
    strokeColor = "#F97316"; // Orange
  }

  // Calculate stroke dash array and offset for circular progress
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (actualOccupancyRate / 100) * circumference;

  const handleDeleteScreen = async () => {
    try {
      setIsDeleting(true);
      await deleteScreen(screen.id);
      toast.success(`Pantalla "${screen.name}" eliminada correctamente`);
    } catch (error) {
      console.error("Error al eliminar pantalla:", error);
      toast.error("Error al eliminar la pantalla");
    } finally {
      setIsDeleting(false);
      setConfirmDialogOpen(false);
    }
  };

  const handleToggleFavorite = () => {
    toggleFavoriteScreen(screen.id);
    toast.success(isFavorite 
      ? `Pantalla "${screen.name}" eliminada de favoritos` 
      : `Pantalla "${screen.name}" agregada a favoritos`);
  };

  const handleRemoveExpiredAd = (adId: string) => {
    console.log("Iniciando proceso de remoción para anuncio:", adId);
    setSelectedExpiredAdId(adId);
    setConfirmDeleteAdDialogOpen(true);
  };

  const confirmRemoveExpiredAd = async () => {
    if (!selectedExpiredAdId) {
      console.error("No hay anuncio seleccionado para ocultar");
      return;
    }

    setIsHidingAd(true);
    try {
      console.log("Llamando a hideExpiredAd para:", selectedExpiredAdId);
      await hideExpiredAd(selectedExpiredAdId);
      console.log("Proceso de ocultación completado exitosamente");
    } catch (error) {
      console.error("Error en confirmRemoveExpiredAd:", error);
      toast.error("Error al ocultar el anuncio");
    } finally {
      setIsHidingAd(false);
      setConfirmDeleteAdDialogOpen(false);
      setSelectedExpiredAdId(null);
    }
  };

  const canRemoveExpiredAd = (adSellerId: string) => {
    return isAdmin || adSellerId === user?.id;
  };

  return (
    <>
      <Card className="w-full overflow-hidden transition-all duration-500 hover:shadow-[0_4px_12px_-6px_rgba(0,0,0,0.08)] hover:transform hover:-translate-y-[2px] bg-white focus:outline-none select-none">
        <CardContent className="p-6">
          {/* Header with title and percentage */}
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-1 uppercase tracking-wide">
                {screen.name}
              </h3>
              <div className="flex items-center text-gray-600 text-sm">
                <MapPin className="w-4 h-4 mr-1" />
                {screen.location}
              </div>
            </div>
            
            {/* Circular percentage indicator */}
            <div className="relative flex items-center justify-center ml-4">
              <svg width="110" height="110" viewBox="0 0 110 110" className="transform -rotate-90">
                {/* Background circle */}
                <circle
                  cx="55"
                  cy="55"
                  r={radius}
                  fill="transparent"
                  stroke="#E5E7EB"
                  strokeWidth="8"
                />
                {/* Progress circle */}
                <circle
                  cx="55"
                  cy="55"
                  r={radius}
                  fill="transparent"
                  stroke="#F59E0B"
                  strokeWidth="8"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900">{actualOccupancyRate}%</span>
                <span className="text-xs text-gray-600 font-medium">Ocupado</span>
              </div>
            </div>
          </div>
          
          {/* Spaces information */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Espacios</span>
              <span className="text-lg font-bold text-gray-900">{actualOccupiedSpaces}/{screen.totalSpaces}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-yellow-400 rounded-full transition-all"
                style={{ width: `${actualOccupancyRate}%` }}
              />
            </div>
          </div>
          
          {/* Active ads count */}
          <div className="mb-6 flex items-center text-gray-700 text-sm">
            <Users className="w-4 h-4 mr-2" /> 
            <span className="font-medium">{activeAds.length} anuncios activos</span>
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-between items-center mt-4">
            <Button 
              onClick={() => setSheetOpen(true)}
              className="bg-[#1e3a4a] hover:bg-[#152e3a] text-white px-6 py-2 rounded-md font-medium transition-colors"
            >
              Ver anuncios
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleToggleFavorite}
                aria-label={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
                title={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
                className="h-10 w-10 bg-[#1e3a4a] hover:bg-[#152e3a] border-[#1e3a4a]"
              >
                {isFavorite ? (
                  <BookmarkCheck className="w-4 h-4 text-white fill-white" />
                ) : (
                  <Bookmark className="w-4 h-4 text-white" />
                )}
              </Button>
              
              {isAdmin && (
                <>
                  <Button
                    variant="outline" 
                    size="icon"
                    onClick={() => onEdit?.(screen)}
                    aria-label="Editar pantalla"
                    title="Editar pantalla"
                    className="h-10 w-10 text-blue-500 hover:bg-blue-50 border-blue-200"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline" 
                    size="icon"
                    onClick={() => setConfirmDialogOpen(true)}
                    aria-label="Eliminar pantalla"
                    className="h-10 w-10 text-red-500 hover:bg-red-50 border-red-200"
                  >
                    <Trash className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg md:max-w-xl h-[100vh] max-h-[100vh] flex flex-col p-0">
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              <SheetHeader className="mb-6">
                <SheetTitle>Anuncios de "{screen.name}"</SheetTitle>
                <SheetDescription>
                  Aquí se muestran los anuncios activos y vencidos visibles para esta pantalla.
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6">
                {/* Sección anuncios activos */}
                <div>
                  <h3 className="font-medium text-lg mb-3">Anuncios Activos</h3>
                  {activeAds.length === 0 ? (
                    <div className="text-center p-6 bg-gray-50 rounded-md text-muted-foreground">
                      <MonitorPlay className="mx-auto mb-2 h-8 w-8 opacity-40" />
                      No hay anuncios activos para esta pantalla.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {activeAds.map((ad) => (
                        <li key={ad.id} className="border rounded-md p-4 hover:bg-gray-50 transition">
                          <div className="font-medium text-base">{ad.clientName}</div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Vigencia:</span>{" "}
                              <span>{ad.startDate} - {ad.endDate ? ad.endDate : "sin fecha de fin"}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Vendedor:</span>{" "}
                              <span>{ad.sellerName}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Correo:</span>{" "}
                              <span>{ad.sellerEmail && ad.sellerEmail !== "" ? ad.sellerEmail : "Sin correo registrado"}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Monto:</span>{" "}
                              <span className="font-medium">
                                RD${" "}
                                {ad.amount?.toLocaleString("es-DO", {
                                  minimumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            {ad.adType === "courtesy" && (
                              <Badge className="bg-[#ff8a33] hover:bg-[#f07c28] text-white">
                                Cortesía
                              </Badge>
                            )}
                            {ad.materialUrl ? (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-primary border-primary/20 hover:bg-primary/10 hover:border-primary/30"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(ad.materialUrl, '_blank');
                                }}
                              >
                                VER MATERIAL
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-muted-foreground border-border cursor-not-allowed"
                                disabled
                              >
                                SIN MATERIAL
                              </Button>
                            )}
                          </div>
                        </li>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Sección anuncios vencidos */}
                <div>
                  <h3 className="font-medium text-lg mt-6 mb-3">Anuncios Vencidos</h3>
                  {expiredAds.length === 0 ? (
                    <div className="text-center p-6 bg-muted rounded-md text-muted-foreground">
                      <MonitorPlay className="mx-auto mb-2 h-8 w-8 opacity-40" />
                      No hay anuncios vencidos visibles para esta pantalla.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {expiredAds.map((ad) => (
                        <li key={ad.id} className="border border-destructive/20 bg-destructive/5 rounded-md p-4 relative">
                          {canRemoveExpiredAd(ad.sellerId) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveExpiredAd(ad.id)}
                              className="absolute top-2 right-2 h-8 w-8 text-destructive hover:bg-destructive/10"
                              aria-label="Remover anuncio vencido"
                              title="Ocultar permanentemente"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                          <div className="font-medium text-base flex justify-between pr-10">
                            {ad.clientName}
                            <Badge variant="destructive">Vencido</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Vigencia:</span>{" "}
                              <span>{ad.startDate} - {ad.endDate ? ad.endDate : "sin fecha de fin"}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Vendedor:</span>{" "}
                              <span>{ad.sellerName}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Correo:</span>{" "}
                              <span>{ad.sellerEmail && ad.sellerEmail !== "" ? ad.sellerEmail : "Sin correo registrado"}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Monto:</span>{" "}
                              <span className="font-medium">
                                RD${" "}
                                {ad.amount?.toLocaleString("es-DO", {
                                  minimumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            {ad.adType === "courtesy" && (
                              <Badge className="bg-[#ff8a33] hover:bg-[#f07c28] text-white">
                                Cortesía
                              </Badge>
                            )}
                            {ad.materialUrl ? (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-primary border-primary/20 hover:bg-primary/10 hover:border-primary/30"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(ad.materialUrl, '_blank');
                                }}
                              >
                                VER MATERIAL
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-muted-foreground border-border cursor-not-allowed"
                                disabled
                              >
                                SIN MATERIAL
                              </Button>
                            )}
                          </div>
                        </li>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-background">
            <Button variant="outline" onClick={() => setSheetOpen(false)}>
              Cerrar
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Diálogo de confirmación para eliminar pantalla */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la pantalla "{screen.name}" permanentemente y no se puede deshacer.
              {(activeAds.length > 0 || expiredAds.length > 0) && (
                <p className="mt-2 text-red-500 font-medium">
                  ¡Advertencia! Esta pantalla tiene {activeAds.length + expiredAds.length} anuncio(s) asignado(s) ({activeAds.length} activos y {expiredAds.length} vencidos). 
                  Al eliminar la pantalla, también se eliminarán sus anuncios.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                handleDeleteScreen();
              }}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? "Eliminando..." : "Eliminar pantalla"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de confirmación mejorado para remover anuncio vencido */}
      <AlertDialog open={confirmDeleteAdDialogOpen} onOpenChange={setConfirmDeleteAdDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Remover anuncio vencido?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción ocultará el anuncio vencido permanentemente para todos los usuarios. El anuncio se mantendrá en el historial de anuncios vencidos del sistema, pero ya no será visible en esta pantalla.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isHidingAd}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmRemoveExpiredAd}
              className="bg-red-600 hover:bg-red-700"
              disabled={isHidingAd}
            >
              {isHidingAd ? "Ocultando..." : "Ocultar permanentemente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ScreenCard;
